import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import type { DynamicFieldData } from '../SourceCreate.types';
import { processAllFields } from './DataProvider.fieldProcessing';
import { isActivateToggleEnabled } from './DataProvider.service';
import type { UseDataProviderFieldsOptions } from './DataProvider.types';

/**
 * Custom hook that manages field processing and form registration for data provider rows.
 *
 * **Responsibilities:**
 * 1. Processes fields through validation and business rule pipeline
 * 2. Registers/unregisters fields with React Hook Form based on selection state
 * 3. Handles field state transitions when provider is selected/unselected
 * 4. Manages field visibility and auto-expansion
 *
 * **Key Behaviors:**
 * - When selected: Apply validation, register fields, auto-expand row
 * - When unselected: Remove validation, unregister fields, clear errors
 * - On every change: Reprocess fields through business rule pipeline
 *
 * @param options - Configuration for field processing
 * @returns Array of processed fields ready for rendering
 *
 * @example
 * const processedFields = useDataProviderFields({
 *   isSelected: true,
 *   parentName: 'data_provider.AWSGuardDuty',
 *   mode: 'Edit',
 *   sourceInputs: dataSource.source_inputs,
 *   selectedSourceDetails,
 *   dataSourceName: 'AWSGuardDuty'
 * });
 */
export const useDataProviderFields = (
  options: UseDataProviderFieldsOptions
): DynamicFieldData[] => {
  const {
    isSelected,
    parentName,
    mode,
    sourceInputs,
    selectedSourceDetails,
    dataSourceName
  } = options;

  const { clearErrors, unregister } = useFormContext();
  // Process fields through validation and business rule pipeline
  const processedFields = processAllFields(sourceInputs, {
    isSelected,
    parentName,
    mode,
    selectedSourceDetails,
    dataSourceName,
    isActivateToggleEnabledFn: isActivateToggleEnabled
  });

  useEffect(() => {
    if (isSelected) return;

    // Unselected: Clear validation but preserve values for re-selection
    sourceInputs?.forEach((field: DynamicFieldData) => {
      const fullFieldName = `${parentName}.${field.name}`;
      clearErrors(fullFieldName);
      unregister(fullFieldName, {
        keepValue: true, // Preserve values for when provider is re-selected
        keepError: false,
        keepDirty: false,
        keepTouched: false,
        keepIsValid: false,
        keepDefaultValue: false
      });
    });
  }, [
    isSelected,
    parentName,
    mode,
    sourceInputs,
    selectedSourceDetails,
    dataSourceName,
    clearErrors,
    unregister
  ]);

  return processedFields;
};
